import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUpcoming } from '../hooks/useUpcoming';
import { useMaerkeDage } from '../hooks/useMaerkeDage';
import { useFoedselsDage } from '../hooks/useFoedselsDage';
import { resolverAlleOfficielle, resolverBrugerBegivenheder, getBegivenhederForDato } from '../utils/dateUtils';
import { EventType } from '../types';
import UpcomingList from '../components/upcoming/UpcomingList';
import CalendarGrid from '../components/calendar/CalendarGrid';
import DayDetailDrawer from '../components/calendar/DayDetailDrawer';
import DannebrogFlag from '../components/shared/DannebrogFlag';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import styles from './Forside.module.scss';

type FilterType = 'alle' | EventType;

const FILTRE: { id: FilterType; label: string }[] = [
  { id: 'alle',        label: 'Alle' },
  { id: 'officiel',    label: '🇩🇰 Flagdage' },
  { id: 'maerkedag',   label: '📅 Mærkedage' },
  { id: 'foedselsdag', label: '🎂 Fødselsdage' },
];

const Forside: React.FC = () => {
  const { upcoming, loading, error } = useUpcoming(60);
  const { maerkeDage } = useMaerkeDage();
  const { foedselsDage } = useFoedselsDage();

  const [valgtDag, setValgtDag] = useState<Date | null>(null);
  const [aktivFilter, setAktivFilter] = useState<FilterType>('alle');

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const maanedsBegivenheder = useMemo(() => {
    const officielle = resolverAlleOfficielle(currentYear);
    const bruger = resolverBrugerBegivenheder(maerkeDage, foedselsDage, currentYear);
    return [...officielle, ...bruger].filter((b) => b.dato.getMonth() === currentMonth);
  }, [maerkeDage, foedselsDage, currentYear, currentMonth]);

  return (
    <div className={styles.page}>
      {/* Hero */}
      <section
        className={styles.hero}
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/dannebrog.jpg)` }}
      >
        <div className={`${styles.heroInner} container`}>
          <h1 className={styles.heroTitle}>Askevej Flagdage</h1>
          <p className={styles.heroSubtitle}>
            Flagdage og mærkedage på Askevej (og Søvej)
          </p>
        </div>
      </section>

      <div className={`${styles.content} container`}>
          {/* Mini calendar section */}
          <section className={styles.section}>
              <h2 className="section-title">
                  Denne måned
              </h2>
              <div className={styles.calendarCard}>
                  <CalendarGrid
                      year={currentYear}
                      month={currentMonth}
                      begivenheder={maanedsBegivenheder}
                      onDagValgt={setValgtDag}
                      mini
                  />
              </div>
          </section>
        {/* Upcoming section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className="section-title">Kommende dage</h2>
            <Link to="/kalender" className={styles.seeAll}>Se alle →</Link>
          </div>
          <div className={styles.filterRow}>
            <select
              className={styles.filterSelect}
              value={aktivFilter}
              onChange={(e) => setAktivFilter(e.target.value as FilterType)}
              aria-label="Filtrer begivenheder"
            >
              {FILTRE.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </div>
          {loading ? (
            <LoadingSpinner centered />
          ) : error ? (
            <p className={styles.error}>{error}</p>
          ) : (
            <UpcomingList
              begivenheder={aktivFilter === 'alle' ? upcoming : upcoming.filter(b => b.type === aktivFilter)}
              max={5}
            />
          )}
        </section>



        {/* Quick link */}
        <section className={styles.section}>
          <Link to="/notifikationer" className={styles.notifCard}>
            <div>
              <h3 className={styles.notifTitle}>🔔 Tilmeld notifikationer</h3>
              <p className={styles.notifText}>
                Modtag email når der er flagdag eller mærkedag på Askevej
              </p>
            </div>
            <span className={styles.arrow}>→</span>
          </Link>
        </section>
      </div>
      <DayDetailDrawer
        dato={valgtDag}
        begivenheder={valgtDag ? getBegivenhederForDato(maanedsBegivenheder, valgtDag) : []}
        onLuk={() => setValgtDag(null)}
      />
    </div>
  );
};

export default Forside;
